import { useState } from 'react';
import { Package, ITranslation, Resort, YadoHotel, YadoRoom, SearchParams } from '../types';
import Modal from '../components/Modal';
import PackageModalInner from './PackageModalInner';
import styles from '../styles/HotelPlansSearch.module.scss';
import ChevronRight from '../images/chevron-right.svg';
import { getImage } from '../../utils/pathHelper';
import i18n from "../../i18n";
import { getPeopleCountDisplay } from '../utils';
import { addDays, differenceInDays } from 'date-fns';
import { formatDateWithDayOfWeek } from '../../utils/dateHelper';

type PackageItemProps = {
  packageItem: Package;
  hotel: YadoHotel;
  room: YadoRoom;
  localizedItemName: ITranslation;
  localizedAttributes: {
    mainAmenities: Record<string, string | string[]>;
    otherAmenities: Record<string, string>;
  };
  isIncludingTicketItem?: boolean;
  icons: {
    foodIcon: string;
    noFoodIcon: string;
    hotelIcon: string;
    smokingIcon: string;
    noSmokingIcon: string;
    wifiIcon: string;
    yadoIcon: string;
    bedIcon: string;
    onsenIcon: string;
    ticketIcon: string;
    ticketIconSolid: string;
    skiIcon: string;
    commonTicketIcon: string;
    plusIcon: string;
  };
  query: SearchParams;
  currency: string;
};

const PackageItem: React.FC<PackageItemProps> = ({
  packageItem,
  hotel,
  room,
  localizedItemName,
  localizedAttributes,
  icons,
  query,
  currency,
}) => {
  const [isModalShown, setModalShown] = useState<boolean>(false);
  const gon = (window as any).gon;
  const locale = gon.locale;

  const title = `${room.localized_name[locale]} + ${localizedItemName[locale]}`;
  const priceDisplay = locale === 'ja' ? `${packageItem.total_price.amount}${currency}` : `${currency} ${packageItem.total_price.amount}`;

  const peopleCountDisplay = getPeopleCountDisplay(query.people);
  const checkinDatesDisplay = `${formatDateWithDayOfWeek(query.checkinDate)} - ${formatDateWithDayOfWeek(query.checkoutDate)}`;
  const freeCancellationPolicy = room.yado_plan.cancellation_policies.find((policy) => policy.penalty_rate === 0)
  const freeCancellationDeadline = freeCancellationPolicy ? addDays(query.checkinDate, -freeCancellationPolicy.remaining_days) : addDays(query.checkinDate, -90);

  return (
    <div className={`${styles.package} d-flex flex-column flex-md-row align-items-center py-3 position-relative`}>
      <div className="d-flex flex-row flex-wrap align-items-start mb-3 mb-md-0 w-100 position-relative">
        <div className={`${styles.packageImageWrapper} col-4 mb-3 mb-md-0`}>
          <img
            src={packageItem.common_resorts[0]?.thumb_image_url}
            alt={localizedItemName[locale]}
            className={`${styles.packageImage} img-fluid rounded`}
          />
          <img src={icons.plusIcon} alt="Plus Icon" className={styles.plusIcon} />
        </div>
        <div className={`${styles.packageInfo} col-8 flex-grow-1`}>
          {packageItem.common_resorts.length > 1 && (
            <div className={`${styles.ticketBadge} d-flex align-items-center mb-2`}>
              <img className="mr-2" src={icons.commonTicketIcon} alt="common ticket icon" />
              <span>{i18n.t('accommodations.common_lift_ticket')}</span>
            </div>
          )}
          <p className={`mb-0 ${styles.snowItemModalLink}`}>
            {packageItem.snow_item?.id ? (
              <a href="#" data-toggle="modal" data-target={`#snow-item-details-${packageItem.snow_item?.id}`} className={`stretched-link ${styles.snowItemModalLink}`}>
                {localizedItemName[locale]}
              </a>
            ) : (
              localizedItemName[locale]
            )}
          </p>
          <p className="text-muted">
            {i18n.t('stay_packages.hotels.show.limited_to_following_resorts')}
          </p>
          <ul>
            {packageItem.common_resorts.map((resort) => (
              <li key={resort.resort_code}>{resort.localized_name[locale]}</li>
            ))}
          </ul>
          {packageItem.snow_item?.id && (
            <div className="d-flex align-items-center">
              <p className="text-secondary mb-0">{i18n.t('base.see_detail')}</p>
              <img src={getImage(ChevronRight)} className="mr-2" />
            </div>
          )}
        </div>
        {/* Desktop view */}
        <div className="d-none d-md-flex align-items-center justify-content-between w-100">
          <div style={{marginBottom: '12px'}}>
            <div className="text-center d-flex align-items-center">
              <div className='mr-2'>{peopleCountDisplay}</div>
              <div className='mr-2'>
                {differenceInDays(query.checkoutDate, query.checkinDate) === 1
                  ? i18n.t('base.number_of_stays.one')
                  : i18n.t('base.number_of_stays.other', { count: differenceInDays(query.checkoutDate, query.checkinDate) })
                }
              </div>
            </div>
            <div className='text-muted mr-2'>
              {packageItem.ticket_splits.adults + packageItem.ticket_splits.children === 1 ? (
                `${i18n.t('stay_packages.shared.with_passes')} : ${i18n.t('stay_packages.shared.x_passes.one')}`
              ) : (
                `${i18n.t('stay_packages.shared.with_passes')} : ${i18n.t('stay_packages.shared.x_passes.other', { x: packageItem.ticket_splits.adults + packageItem.ticket_splits.children })}`
              ) }
            </div>
          </div>
          <div className="text-center d-flex align-items-center">
            { i18n.locale !== 'ja' && <div className='text-muted text-sm mr-2'>{packageItem.total_price_ja.amount}</div> }
            <div className={`text-primary ${styles.packagePrice} mr-3`}>{priceDisplay}</div>
            <button
              className="btn btn-primary rounded-pill font-weight-bold"
              type='button'
              style={{ minWidth: '175px' }}
              onClick={() => setModalShown(true)}
              >
              {i18n.t('accommodations.proceed_to_booking')}
            </button>
          </div>
        </div>
      </div>
      {/* Mobile view */}
      <div className="d-flex d-md-none flex-row justify-content-between align-items-center w-100 mt-2 mb-3">
        <div className='d-flex flex-column'>
          <div className='d-flex'>
            <div className='mr-2'>{peopleCountDisplay}</div>
            <div>
              {differenceInDays(query.checkoutDate, query.checkinDate) === 1
                ? i18n.t('base.number_of_stays.one')
                : i18n.t('base.number_of_stays.other', { count: differenceInDays(query.checkoutDate, query.checkinDate) })
              }
            </div>
          </div>
          <div className='text-muted mr-2'>
            {packageItem.ticket_splits.adults + packageItem.ticket_splits.children === 1 ? (
                `${i18n.t('stay_packages.shared.with_passes')} : ${i18n.t('stay_packages.shared.x_passes.one')}`
              ) : (
                `${i18n.t('stay_packages.shared.with_passes')} : ${i18n.t('stay_packages.shared.x_passes.other', { x: packageItem.ticket_splits.adults + packageItem.ticket_splits.children })}`
              ) }
          </div>
        </div>
        <div className='d-flex flex-column'>
          { i18n.locale !== 'ja' && <div className='text-muted text-sm text-right'>{packageItem.total_price_ja.amount}</div> }
          <div className={`text-primary ${styles.packagePrice} mb-1`}>{priceDisplay}</div>
        </div>
      </div>
      <button
        className="btn btn-primary rounded-pill font-weight-bold d-md-none"
        style={{ minWidth: '175px' }}
        onClick={() => setModalShown(true)}
      >
        {i18n.t('accommodations.proceed_to_booking')}
      </button>
      <Modal
        isShown={isModalShown}
        handleClose={() => {
          setModalShown(false);
        }}
        title={title}
        peopleCountDisplay={peopleCountDisplay}
        checkinDatesDisplay={checkinDatesDisplay}
        freeCancellable={room.yado_plan.cancellable && !!freeCancellationPolicy}
        freeCancellationDeadline={freeCancellationDeadline}
        query={query}
      >
        <PackageModalInner
          packageItem={packageItem}
          hotel={hotel}
          room={room}
          priceDisplay={priceDisplay}
          localizedItemName={localizedItemName}
          localizedAttributes={localizedAttributes}
          icons={icons}
          query={query}
        />
      </Modal>
    </div>
  );
};

export default PackageItem;
